import React, { createContext, useContext, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useUser } from '../../hooks/queries/useAuth';
import { subscriptionManager } from '../../lib/subscription-manager';
import { logger } from '../../utils/logger';
import { projectKeys } from '../../hooks/queries/projectKeys';
import { notificationKeys } from '../../hooks/queries/notificationKeys';
import { dashboardKeys } from '../../hooks/queries/dashboardKeys';
import { collaboratorKeys } from '../../hooks/queries/collaboratorKeys';
import { db } from '../../lib/offline-db';
import { CLIENT_ID } from '../../lib/client-context';

// Create context for tracking local operations
interface RealtimeContextType {
  trackLocalOperation: (entityId: string, entityType: string) => void;
}

const RealtimeContext = createContext<RealtimeContextType | null>(null);

/**
 * Hook to access realtime tracking functionality
 * Used to track local operations to prevent duplicate processing
 */
export function useRealtimeTracking() {
  const context = useContext(RealtimeContext);

  if (!context) {
    throw new Error('useRealtimeTracking must be used within a RealtimeQueryProvider');
  }

  return context;
}

/**
 * Global provider component for app-wide realtime subscriptions
 * This handles subscriptions that should exist regardless of which
 * page the user is currently viewing.
 */
export function RealtimeQueryProvider({ children }: { children: React.ReactNode }) {
  // Use ref to store operations being tracked across renders
  const localOperationsRef = useRef<Map<string, Set<string>>>(new Map());
  const queryClient = useQueryClient();
  const { data: user } = useUser();

  // Set up global realtime subscriptions
  useEffect(() => {
    if (!user?.id) return;

    logger.realtime('Setting up global realtime subscriptions', { userId: user.id });

    const setupSubscriptions = async () => {
      try {
        // Subscribe to project changes
        const projectUnsubscribe = await subscriptionManager.subscribe(
          {
            channelName: `global-projects-${user.id}`,
            table: 'projects',
            event: '*',
            filter: `user_id=eq.${user.id}`,
          },
          payload => {
            logger.realtime('Global project change detected', {
              event: payload.eventType,
              projectId: payload.new?.id || payload.old?.id,
            });

            // Invalidate project queries
            queryClient.invalidateQueries({
              queryKey: projectKeys.lists(),
              refetchType: 'active',
            });
          }
        );

        // Subscribe to project collaborator changes
        const collaboratorUnsubscribe = await subscriptionManager.subscribe(
          {
            channelName: `global-collaborators-${user.id}`,
            table: 'project_collaborators',
            event: '*',
            filter: `user_id=eq.${user.id}`,
          },
          payload => {
            logger.realtime('Global collaborator change detected', {
              event: payload.eventType,
              projectId: payload.new?.project_id || payload.old?.project_id,
            });

            // Invalidate project lists
            queryClient.invalidateQueries({
              queryKey: projectKeys.lists(),
              refetchType: 'active',
            });

            // Invalidate all collaborator queries for better real-time sync
            queryClient.invalidateQueries({
              queryKey: collaboratorKeys.all,
              refetchType: 'active',
            });
          }
        );

        // Subscribe to notifications
        const notificationUnsubscribe = await subscriptionManager.subscribe(
          {
            channelName: `global-notifications-${user.id}`,
            table: 'notifications',
            event: '*',
            filter: `user_id=eq.${user.id}`,
          },
          payload => {
            logger.realtime('Global notification change detected', {
              event: payload.eventType,
              notificationId: payload.new?.id || payload.old?.id,
            });

            // Invalidate notification queries
            queryClient.invalidateQueries({
              queryKey: notificationKeys.lists(),
              refetchType: 'active',
            });
          }
        );

        // Handler for dashboard task events
        const handleTaskEvent = async (payload: any) => {
          const eventType = payload.eventType;
          const taskId = payload.new?.id || payload.old?.id;

          // Check if this is a local operation we should ignore
          const taskOps = localOperationsRef.current.get('task');
          if (taskId && taskOps?.has(taskId)) {
            logger.realtime('Skipping dashboard update for tracked local operation', {
              taskId,
              eventType,
            });

            // Remove this taskId from tracking
            taskOps.delete(taskId);
            return;
          }

          // Check if this event was caused by this client
          const eventClientId =
            payload.new?.metadata?.client_id || payload.old?.metadata?.client_id;
          if (eventClientId === CLIENT_ID) {
            logger.realtime('Skipping own task event in dashboard', {
              taskId,
              eventType,
              clientId: CLIENT_ID,
            });
            return;
          }

          // Check if this task change is dashboard-relevant:
          // 1. Task has a deadline within the next week, OR
          // 2. Task is highlighted, OR
          // 3. Task status changed to/from 'done'
          const oldTask = payload.old;
          const newTask = payload.new;
          const isDashboardRelevant =
            // Task is/was highlighted
            newTask?.is_highlighted === true ||
            oldTask?.is_highlighted === true ||
            // Task has/had deadline (we'll check date range in client)
            newTask?.deadline ||
            oldTask?.deadline ||
            // Task status changed to/from 'done'
            (newTask?.status !== oldTask?.status &&
              (newTask?.status === 'done' || oldTask?.status === 'done'));

          if (isDashboardRelevant) {
            logger.realtime('Dashboard-relevant task change detected', {
              event: eventType,
              taskId,
              taskStatus: newTask?.status || oldTask?.status,
              wasHighlighted: oldTask?.is_highlighted,
              isHighlighted: newTask?.is_highlighted,
              wasDeadline: oldTask?.deadline,
              isDeadline: newTask?.deadline,
              timestamp: new Date().toISOString(),
            });

            // Use setTimeout to batch multiple rapid changes
            setTimeout(async () => {
              // Determine which specific dashboard sections to invalidate
              const sectionsToInvalidate = new Set<
                'highlighted' | 'overdue' | 'today' | 'thisWeek'
              >();

              // If task is/was highlighted, invalidate highlighted section
              if (newTask?.is_highlighted === true || oldTask?.is_highlighted === true) {
                sectionsToInvalidate.add('highlighted');
              }

              // If task has a deadline, it might be in overdue, today, or thisWeek sections
              if (newTask?.deadline || oldTask?.deadline) {
                // We can't easily determine which section(s) without fetching more data,
                // so conservatively invalidate all deadline-related sections
                sectionsToInvalidate.add('overdue');
                sectionsToInvalidate.add('today');
                sectionsToInvalidate.add('thisWeek');
              }

              // If task status changed to/from 'done', it might affect any section
              if (
                newTask?.status !== oldTask?.status &&
                (newTask?.status === 'done' || oldTask?.status === 'done')
              ) {
                // Status change to/from done - invalidate all sections
                sectionsToInvalidate.add('highlighted');
                sectionsToInvalidate.add('overdue');
                sectionsToInvalidate.add('today');
                sectionsToInvalidate.add('thisWeek');
              }

              // If assignee changed, invalidate user-specific dashboard data
              if (newTask?.assigned_to !== oldTask?.assigned_to) {
                if (oldTask?.assigned_to) {
                  queryClient.invalidateQueries({
                    queryKey: dashboardKeys.byUser(oldTask.assigned_to),
                    refetchType: 'active',
                  });
                }
                if (newTask?.assigned_to) {
                  queryClient.invalidateQueries({
                    queryKey: dashboardKeys.byUser(newTask.assigned_to),
                    refetchType: 'active',
                  });
                }
              }

              // Invalidate specific sections if we determined which ones are affected
              if (sectionsToInvalidate.size > 0) {
                // If all sections are affected, just invalidate all dashboard content
                if (sectionsToInvalidate.size === 4) {
                  queryClient.invalidateQueries({
                    queryKey: dashboardKeys.content(),
                    refetchType: 'active',
                  });
                } else {
                  // Otherwise, invalidate only the affected sections
                  sectionsToInvalidate.forEach(section => {
                    queryClient.invalidateQueries({
                      queryKey: dashboardKeys.section(section),
                      refetchType: 'active',
                    });
                  });

                  // Also invalidate the specific task
                  if (taskId) {
                    queryClient.invalidateQueries({
                      queryKey: dashboardKeys.task(taskId),
                      refetchType: 'active',
                    });

                    // Store the updated task in IndexedDB if we have task data
                    try {
                      if (payload.new && payload.eventType !== 'DELETE') {
                        await db.tasks.put(payload.new);
                        logger.fetch(
                          `Stored updated task ${taskId} in IndexedDB from subscription`
                        );
                      } else if (payload.eventType === 'DELETE') {
                        await db.tasks.delete(taskId);
                        logger.fetch(`Removed deleted task ${taskId} from IndexedDB`);
                      }
                    } catch (storageError) {
                      logger.error(
                        `Error updating task in IndexedDB from subscription:`,
                        storageError
                      );
                      // Continue even if storage fails - this is non-critical
                    }
                  }
                }
              } else {
                // If we couldn't determine specific sections, invalidate all content
                queryClient.invalidateQueries({
                  queryKey: dashboardKeys.content(),
                  refetchType: 'active',
                });
              }
            }, 500);
          }
        };

        // Subscribe to task changes for dashboard
        const dashboardTasksUnsubscribe = await subscriptionManager.subscribe(
          {
            channelName: `global-dashboard-tasks`,
            table: 'tasks',
            event: '*',
            schema: 'public',
          },
          handleTaskEvent
        );

        // Clean up all subscriptions when component unmounts or user changes
        return () => {
          projectUnsubscribe();
          collaboratorUnsubscribe();
          notificationUnsubscribe();
          dashboardTasksUnsubscribe();
          logger.realtime('Cleaned up global realtime subscriptions', { userId: user.id });
        };
      } catch (error) {
        logger.error('Error setting up global realtime subscriptions', {
          error: error instanceof Error ? error.message : String(error),
          userId: user.id,
        });

        // Return empty cleanup function
        return () => {};
      }
    };

    // Setup subscriptions and get cleanup function
    let cleanup: (() => void) | undefined;
    setupSubscriptions().then(cleanupFn => {
      cleanup = cleanupFn;
    });

    // Return cleanup function for useEffect
    return () => {
      if (cleanup) {
        cleanup();
      }
    };
  }, [user?.id]);

  // Create context value with tracking function
  const contextValue: RealtimeContextType = {
    trackLocalOperation: (entityId: string, entityType: string = 'task') => {
      // Get or create the set for this entity type
      if (!localOperationsRef.current.has(entityType)) {
        localOperationsRef.current.set(entityType, new Set());
      }

      // Add the entity ID to the tracking set
      const entitySet = localOperationsRef.current.get(entityType)!;
      entitySet.add(entityId);

      logger.realtime(`Tracking ${entityType} operation`, { entityId });

      // Auto-clean after 30 seconds
      setTimeout(() => {
        const currentSet = localOperationsRef.current.get(entityType);
        if (currentSet?.has(entityId)) {
          currentSet.delete(entityId);
          logger.realtime(`Auto-cleaned tracked ${entityType} operation`, { entityId });
        }
      }, 30000);
    },
  };

  // Provide context to children
  return <RealtimeContext.Provider value={contextValue}>{children}</RealtimeContext.Provider>;
}

export default RealtimeQueryProvider;
